import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Twitter, Mail } from "lucide-react";

const teamMembers = [
  {
    id: 1,
    name: "CA Manish Gupta",
    role: "Chartered Accountant | Startup Advisor & Virtual CFO",
    bio: "CA Manish Gupta is a Chartered Accountant with over 16 years of professional experience in Startup Valuation, CFO Services, Regulatory Compliances and Taxation Advisory. With hands-on exposure across multiple Service Sectors & Manufacturing industry he brings end to end guide for any business-related challenges and help in better planning of funds.",
    expertise: ["ESOP & F&O Taxation Advisory", "Ideal Fund Management", "Taxation & Regulatory Compliance", "Startup Valuation & Funding", "Audit & Assurance"],
    image: "/finease-x/CA Manish Gupta.jpeg",
    linkedin: "https://linkedin.com",
    email: "manish@fineaseconsulting.com",
  },
  {
    id: 2,
    name: "CA Abhishek G N",
    role: "Chartered Accountant | Startup & Business Advisory Specialist",
    bio: "CA Abhishek G N is a Chartered Accountant with over 13 years of professional experience in finance, taxation, and regulatory compliance. He advises startups, SMEs, and corporates on building strong financial foundations and ensuring seamless statutory compliance while supporting long-term business growth. His practice focuses on helping entrepreneurs move from idea stage to scale-up stage with structured financial systems and compliant operations.",
    expertise: ["Accounting & Financial Management", "Taxation & Regulatory Compliance", "Startup & Business Advisory", "Audit & Assurance"],
    image: "/finease-x/CA Abhishek GN.jpeg",
    linkedin: "https://linkedin.com",
    email: "abhishek@fineaseconsulting.com",
  },
  {
    id: 3,
    name: "CS Garima Goyal",
    role: "Company Secretary | Corporate Law & Compliance Expert",
    bio: "CS Garima Goyal is a qualified Company Secretary with extensive experience in corporate law, secretarial compliance, and SEBI regulations. She specializes in company incorporations, ROC filings, and corporate governance advisory, helping businesses maintain statutory compliance and handle board-related matters with precision.",
    expertise: ["Company Incorporation", "ROC Compliance", "Corporate Governance", "SEBI Regulations"],
    image: "/finease-x/CS Garima Goyal.jpeg",
    linkedin: "https://linkedin.com",
    email: "garima@fineaseconsulting.com",
  },
  {
    id: 4,
    name: "CS Pooja Kansal",
    role: "Company Secretary | Secretarial & Legal Specialist",
    bio: "CS Pooja Kansal is a qualified Company Secretary with expertise in legal documentation, contract drafting, and corporate restructuring. She has extensive experience in handling mergers, acquisitions, and due diligence processes, providing end-to-end secretarial support to clients across industries.",
    expertise: ["Legal Documentation", "Contract Drafting", "M&A Due Diligence", "Corporate Restructuring"],
    image: "/finease-x/CS Pooja Kansal.jpeg",
    linkedin: "https://linkedin.com",
    email: "pooja@fineaseconsulting.com",
  },
];

const values = [
  {
    title: "Professional Excellence",
    description: "We uphold the highest standards of professional ethics and deliver expert financial and legal guidance to every client.",
  },
  {
    title: "Client-Centric Approach",
    description: "Your success is our priority. We build lasting relationships by understanding your unique business needs and goals.",
  },
  {
    title: "Comprehensive Solutions",
    description: "From taxation to compliance, we offer end-to-end services covering all aspects of your business financial requirements.",
  },
  {
    title: "Trust & Integrity",
    description: "We operate with complete transparency and confidentiality, building trust through every engagement.",
  },
];

export default function TeamPage() {
  return (
    <main data-testid="team-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Our Team
          </span>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Meet Our Experts
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl">
            A team of qualified Chartered Accountants and Company Secretaries dedicated to helping businesses 
            navigate complex financial and regulatory landscapes with confidence.
          </p>
        </motion.div>
      </section>

      {/* Team Members - All Together */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`team-member-${member.id}`}
              className="team-card group relative bg-[#0A0A0A] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00C896]/20 transition-all"
            >
              <div className="flex flex-col">
                {/* Image */}
                <div className="relative w-full aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3
                    className="text-lg font-semibold text-white mb-1"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {member.name}
                  </h3>
                  <p className="text-xs text-[#00C896] mb-3">{member.role}</p>
                  <p className="text-slate-400 text-xs leading-relaxed mb-4">
                    {member.bio}
                  </p>
                  
                  {/* Expertise Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {member.expertise.map((skill, i) => (
                      <span
                        key={i}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300 border border-white/10"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Social Links */}
                  <div className="flex gap-2">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/30 transition-colors"
                    >
                      <Linkedin size={14} />
                    </a>
                    <a
                      href={"mailto:" + member.email}
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/30 transition-colors"
                    >
                      <Mail size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
              Our Values
            </span>
            <h2
              className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              What Drives Us
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              The principles that guide every decision and interaction at Finease.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-[#0A0A0A] border border-white/5"
              >
                <h3
                  className="text-xl font-semibold text-white mb-3"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {value.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Ready to Work Together?
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              Let's discuss how our team can help you achieve your financial goals.
            </p>
            <Link
              to="/contact"
              data-testid="team-page-cta"
              className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)]"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
